// Telefonassistent (Vocaris) – reine Funktionen und Festwerte für Server, Oberfläche und Einrichtungsskript
// (docs/integration/telefonassistent.md). Keine Datenbank, keine Uhr.

/** Rechte der Rolle „Telefonassistent“ (`api_phone`) – das Einrichtungsskript vergibt genau diese (TA-D2). */
export const PHONE_PERMISSIONS = ['sales.phone.read', 'sales.phone.lead', 'sales.phone.orderLookup'] as const

/**
 * Was ein Schlüssel mit genau den Rechten der Rolle in `sales.channel.capabilities` meldet – Vocaris prüft gegen dieselbe
 * Liste (`ROLLE_TELEFON` im Adapter); jede Aktion darüber hinaus gilt dort als Zusatzrecht (TA-R36).
 */
export const PHONE_ACTIONS = [
  'sales.channel.capabilities',
  'sales.phone.availability',
  'sales.phone.cases',
  'sales.phone.catalog',
  'sales.phone.checkCoupon',
  'sales.phone.checkoutRules',
  'sales.phone.lookupOrder',
  'sales.phone.slotLoad',
  'sales.phone.submitCase',
] as const

/** Rollenname in `capabilities.access.role` (Spezifikation Vocaris §3) */
export const PHONE_ROLE_NAME = 'telefonassistent'

/** Kontingent je Schlüssel des Telefonassistenten und Minute (Standard von MERXO_PHONE_API_PER_MINUTE, TA-R14) */
export const PHONE_PER_MINUTE = 120
/** davon Auftragsauskünfte je Zugang und Minute (TA-R14, gezählt in der Datenbank) */
export const PHONE_LOOKUP_PER_MINUTE = 30

/** Kundennummern vergleichen wie am Telefon genannt: ohne Leerraum am Rand, ohne Unterschied zwischen Groß- und Kleinschreibung. */
export const sameCustomerNumber = (a: string, b: string): boolean => a.trim().toUpperCase() === b.trim().toUpperCase()

/**
 * Ziffernfolgen, unter denen eine Rufnummer (E.164, „+4989123456“) in den Stammdaten stehen kann: international („4989123456“),
 * mit 00 („004989123456“) und – für deutsche Nummern – national („089123456“). Für den Abgleich mit Kunden (TA-R19, Abweichung TA-D17).
 */
export function phoneDigits(e164: string): string[] {
  const international = e164.replace(/[^0-9]/g, '')
  if (international.length < 7) return []
  const forms = [international, `00${international}`]
  if (international.startsWith('49')) forms.push(`0${international.slice(2)}`)
  return forms
}

/**
 * Rückrufnummer eines Interessenten vereinheitlichen: In Deutschland, Österreich und der Schweiz gehört die nationale 0 nicht hinter die
 * Ländervorwahl – „+49089…“ ist „+4989…“. So zählt die Grenze je Rückrufnummer (TA-D19) beide Schreibweisen als eine Nummer.
 */
export const normalizeLeadPhone = (e164: string): string => e164.trim().replace(/^\+(49|43|41)0+(?=[1-9])/, '+$1')

// ---- Auftragsauskunft (docs/integration/telefonassistent.md §7, Spezifikation Vocaris §8) -------------------------------------------

/** TA-R23: groß, ohne Leerzeichen, `-`, `/`, `.`, `_` – „al-25 119“ → „AL25119“ */
export const normalizeOrderNumber = (value: string): string => value.toUpperCase().replace(/[\s\-/._]/g, '')
/** Ziffernfolge einer (normalisierten) Nummer – Vergleich, wenn die Eingabe keine Buchstaben enthält, und Schlüssel der Sperre */
export const orderNumberDigits = (value: string): string => value.replace(/[^0-9]/g, '')
/** Enthält die Eingabe Buchstaben? Ohne Buchstaben wird nur über die Ziffern verglichen (TA-R23). */
export const hasLetters = (value: string): boolean => /[A-Z]/.test(normalizeOrderNumber(value))

export const ORDER_STAGES = [
  'received',
  'confirmed',
  'in_preparation',
  'ready',
  'ready_for_pickup',
  'out_for_delivery',
  'partially_delivered',
  'delivered',
  'picked_up',
  'not_delivered',
  'cancelled',
] as const
export type OrderStage = (typeof ORDER_STAGES)[number]

export const ORDER_STAGE_LABELS: Record<OrderStage, string> = {
  received: 'eingegangen, wird geprüft',
  confirmed: 'bestätigt',
  in_preparation: 'wird zusammengestellt',
  ready: 'wird verladen',
  ready_for_pickup: 'liegt zur Abholung bereit',
  out_for_delivery: 'unterwegs',
  partially_delivered: 'teilweise geliefert',
  delivered: 'zugestellt',
  picked_up: 'abgeholt',
  not_delivered: 'Zustellung hat nicht geklappt',
  cancelled: 'storniert',
}

const DELIVERED_NOTES = ['delivered', 'partially_invoiced', 'invoiced']
const RECEIVED = ['pending_review', 'pending_approval', 'approved', 'returned', 'pending_credit', 'draft']

/**
 * Stufe eines Auftrags (Spezifikation Vocaris §8.4) – Ableitung wie in der Bestell-App (Befund Z1): „Geliefert“ heißt in Ordaneo, dass
 * der Lieferschein gebucht ist; zugestellt ist der Auftrag erst, wenn kein Lieferschein mehr im Entwurf, lieferbereit oder unterwegs ist.
 * `deliveries` = Zustände der nicht stornierten Lieferscheine des Auftrags.
 */
export function phoneStage(order: { status: string; pickup: boolean; pickedUp: boolean; deliveries: readonly string[] }): OrderStage {
  const { status, pickup, deliveries } = order
  if (status === 'cancelled') return 'cancelled'
  if (pickup && order.pickedUp) return 'picked_up'
  const inTransit = deliveries.includes('in_transit')
  const ready = deliveries.includes('ready')
  const drafting = deliveries.includes('draft')
  if (!inTransit && !ready && !drafting) {
    const delivered = status === 'delivered' || status === 'closed' || (status !== 'partially_delivered' && deliveries.some((entry) => DELIVERED_NOTES.includes(entry)))
    if (delivered) return pickup ? 'picked_up' : 'delivered'
    if (deliveries.includes('not_delivered')) return 'not_delivered'
  }
  if (inTransit) return 'out_for_delivery'
  if (ready) return pickup ? 'ready_for_pickup' : 'ready'
  if (drafting) return 'in_preparation'
  if (status === 'partially_delivered') return 'partially_delivered'
  if (status === 'confirmed') return 'confirmed'
  if (RECEIVED.includes(status)) return 'received'
  return 'received'
}

const QUARTER = 15 * 60_000

/**
 * Ankunftsfenster aus der geplanten Ankunft (§8.3): von = Ankunft − 15 min, auf die Viertelstunde abgerundet; bis = Ankunft + 15 min,
 * auf die Viertelstunde aufgerundet. Zeitpunkte; die Uhrzeit in Europe/Berlin bildet der Aufrufer.
 */
export function etaWindow(plannedEta: Date): { from: Date; to: Date } {
  const at = plannedEta.getTime()
  return { from: new Date(Math.floor((at - QUARTER) / QUARTER) * QUARTER), to: new Date(Math.ceil((at + QUARTER) / QUARTER) * QUARTER) }
}

// ---- Bestellungen am Telefon (docs/integration/telefonassistent.md §16, Entwurf vocaris-tools/ordaneo/entwurf-bestellungen.md) -------
//
// Der Telefonassistent legt Bestellungen nur UNBESTÄTIGT an („Eingegangen“, Herkunft „Telefon (KI)“): nichts ist reserviert, der Vertrieb
// bestätigt. Recht und Aktionen kommen nur mit `channel-setup.ts … --allow-orders` dazu – die Grundmenge oben bleibt unverändert.

/** Recht für die drei Bestell-Verträge – nur zusätzlich zur Grundmenge, nur mit `--allow-orders` (TB-2) */
export const PHONE_ORDER_PERMISSION = 'sales.phone.order'
/** Aktionen, die das Recht öffnet – genau diese, keine Kundenlisten, keine Rechnungen, kein Storno */
export const PHONE_ORDER_ACTIONS = ['sales.phone.checkCustomer', 'sales.phone.quoteOrder', 'sales.phone.submitOrder'] as const
/** Herkunft am Auftrag (`custom_fields.channel`) – neben desk, phone (vom Personal erfasst) und app (A11) */
export const PHONE_ORDER_CHANNEL = 'phone_assistant'
/** Kennung einer Bestellung von Vocaris (Idempotenz, TB-7/2.7) */
export const PHONE_ORDER_ID = /^voc-best-[0-9a-f]{32}$/
/** Bereich der Kanalnummern; die Nummer selbst heißt „T-482913“ (TB-7, A5) – zufällig gezogen, nicht fortlaufend (§16.12) */
export const PHONE_ORDER_FIRST_NO = 100_001
export const PHONE_ORDER_LAST_NO = 999_999
export const phoneOrderNumber = (no: number): string => `T-${no}`

/** Vorgaben am Zugang (änderbar mit --order-max-euro / --order-max-units, A1) */
export const PHONE_ORDER_MAX_GROSS_CENTS = 100_000
export const PHONE_ORDER_MAX_UNITS = 50
/** feste Grenzen je Bestellung (TB-Tabelle 2.6) */
export const PHONE_ORDER_MAX_LINES = 20
export const PHONE_ORDER_MAX_LINE_QTY = 50
/** gegen Massen- und Spaßbestellungen (A3): je Rückrufnummer bzw. Anrufer-Kennung … */
export const PHONE_ORDERS_PER_PHONE_HOUR = 2
export const PHONE_ORDERS_PER_PHONE_DAY = 4
export const PHONE_ORDERS_OPEN_PER_PHONE = 2
/** offene (unbestätigte) Bestellungen zählen für `openPerPhone` nur so lange (§16.12) */
export const PHONE_ORDERS_OPEN_DAYS = 7
/** … und je Zugang: je Stunde fest, je 24 Stunden am Zugang (`order_day_max`, --order-day-max, Standard 20 – §16.12) */
export const PHONE_ORDERS_PER_ACCESS_HOUR = 10
export const PHONE_ORDERS_PER_ACCESS_DAY = 20
/** Stundengrenze eines Zugangs zu seiner Tagesgrenze: mindestens 10, bei höherer Tagesgrenze die Hälfte davon, nie mehr als die Tagesgrenze */
export const accessHourLimit = (dayMax: number): number => Math.min(dayMax, Math.max(PHONE_ORDERS_PER_ACCESS_HOUR, Math.ceil(dayMax / 2)))
/** Unbestätigte Telefon-Bestellungen belegen höchstens diesen Anteil der Kapazität eines Fensters (mindestens einen Platz, §16.12) */
export const PHONE_ORDER_SLOT_SHARE = 0.5
export const phoneSlotShare = (capacity: number): number => (capacity <= 0 ? 0 : Math.max(1, Math.ceil(capacity * PHONE_ORDER_SLOT_SHARE)))
/** Eine späte Übertragung gilt nur, wenn derselbe Warenkorb höchstens so lange vor dem Ja ohne Einwände geprüft wurde (§16.12) */
export const PHONE_ORDER_QUOTE_BEFORE_MS = 15 * 60_000
/** gleicher Warenkorb und Tag von derselben übertragenen Nummer innerhalb dieser Zeit → Hinweis „möglicherweise doppelt“ */
export const PHONE_ORDER_DUPLICATE_MS = 24 * 3_600_000
/** nie bestätigte Telefon-Bestellungen verwirft der nächtliche Lauf so lange nach dem Eingang (A9, §16.12) */
export const PHONE_ORDER_RETENTION_DAYS = 90
/** Kundenprüfung (Kundennummer + Postleitzahl): Fehlversuche in 60 Minuten je Nummer (betriebsweit) und je Zugang */
export const PHONE_CUSTOMER_FAILS_PER_NUMBER = 5
export const PHONE_CUSTOMER_FAILS_PER_ACCESS = 20
/** so lange gilt eine erfolgreiche Kundenprüfung für Prüfen und Absenden (TB-D2: ohne sie kein Geschäftskunde) */
export const PHONE_CUSTOMER_PROOF_MS = 3 * 3_600_000
/** „im Gespräch“: das Ja liegt höchstens so lange zurück – Verstöße gegen Lieferregeln werden abgelehnt (TB-8) */
export const PHONE_ORDER_LIVE_MS = 2 * 60_000
/** ältere Übertragungen lehnt Ordaneo ab (die Warteschlange von Vocaris läuft höchstens 30 Minuten) */
export const PHONE_ORDER_MAX_AGE_MS = 48 * 3_600_000
/** Wunschtag höchstens so weit in der Zukunft */
export const PHONE_ORDER_HORIZON_DAYS = 60

/** Gründe, aus denen eine Bestellung (so) nicht angelegt wird – in `quoteOrder.problems` und als `details.reason` von `submitOrder` */
export const PHONE_ORDER_PROBLEMS = [
  'item_unknown',
  'order_limit',
  'customer_unverified',
  'address_not_allowed',
  'fulfillment_not_offered',
  'zip_not_served',
  'min_order',
  'slot_unknown',
  'slot_closed',
  'slot_cutoff',
  'slot_full',
  'store_closed',
  'carry_not_offered',
] as const
export type PhoneOrderProblem = (typeof PHONE_ORDER_PROBLEMS)[number]

/** Probleme der Lieferregeln: im Gespräch abgelehnt, bei später Übertragung nur Prüfhinweis am Auftrag (TB-8) */
export const PHONE_RULE_PROBLEMS: readonly PhoneOrderProblem[] = ['zip_not_served', 'min_order', 'slot_unknown', 'slot_closed', 'slot_cutoff', 'slot_full', 'store_closed']

/** HTTP-Art eines Problems bei `submitOrder` (Tabelle 2.5): 403 Kunde, 409 Fenster und Markt, sonst 422 */
export function phoneProblemKind(code: PhoneOrderProblem): 'forbidden' | 'conflict' | 'business' {
  if (code === 'customer_unverified') return 'forbidden'
  if (code.startsWith('slot_') || code === 'store_closed') return 'conflict'
  return 'business'
}

/** Hinweise am Auftrag (`custom_fields.phoneOrder.hints`, nicht `checkout.issues` – deren Liste gehört zum Vertrag der Bestell-App) */
export const PHONE_ORDER_HINTS = [
  'stock_short',
  'phone_unconfirmed',
  'age_verbal',
  'address_new',
  'address_multi',
  'late_transfer',
  'pickup_time_open',
  // seit §16.12 nicht mehr vergeben (Geschäftskunden holen am Telefon nicht ab) – bleibt lesbar
  'pickup_business',
  'business_callback',
  'amount_over_limit',
  'possible_duplicate',
] as const
export type PhoneOrderHint = (typeof PHONE_ORDER_HINTS)[number]

export const PHONE_ORDER_HINT_LABELS: Record<PhoneOrderHint, string> = {
  stock_short: 'Bestand reicht nicht',
  phone_unconfirmed: 'Nummer nicht bestätigt',
  age_verbal: 'Alter nur mündlich bestätigt',
  address_new: 'Neue Lieferanschrift',
  address_multi: 'Mehrere Rufnummern an einer Anschrift',
  late_transfer: 'Später übertragen',
  pickup_time_open: 'Abholzeit absprechen',
  pickup_business: 'Abholer prüfen',
  business_callback: 'Geschäftskunde zurückrufen',
  amount_over_limit: 'Über der Betragsgrenze',
  possible_duplicate: 'Möglicherweise doppelt',
}

/**
 * Hinweise, bei denen vor dem Bestätigen ein Rückruf vermerkt sein muss („Rückruf erfolgt“ am Auftrag, §16.12): andere Rückrufnummer als
 * die übertragene, erste Bestellung an diese Anschrift, später übertragen (am Telefon wurde ein Rückruf zugesagt), Geschäftskunde (nur über
 * Kundennummer und Postleitzahl erkannt – Rückruf unter der hinterlegten Nummer).
 */
export const PHONE_CALLBACK_HINTS: readonly PhoneOrderHint[] = ['phone_unconfirmed', 'address_new', 'late_transfer', 'business_callback']
export const needsPhoneCallback = (hints: readonly { code: PhoneOrderHint }[]): boolean => hints.some((hint) => PHONE_CALLBACK_HINTS.includes(hint.code))

/** Was am Auftrag zur Telefon-Bestellung steht (`custom_fields.phoneOrder`) – nie Grundlage einer Rechnung, nur Anzeige und Wiederholung */
export interface StoredPhoneOrder {
  accessId: string | null
  /** Rückrufnummer (E.164) */
  callbackPhone: string | null
  phoneSource: 'caller_id' | 'other' | null
  /** Rückrufnummer = übertragene Nummer des Anrufs */
  phoneConfirmed: boolean
  ageConfirmed: boolean
  callRef: string | null
  /** Zeitpunkt des Ja (ISO) */
  placedAt: string | null
  late: boolean
  /** Geschäftskunde: wer am Telefon bestellt hat */
  contactName: string | null
  hints: { code: PhoneOrderHint; message: string }[]
}

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value.trim() : null)

/** Zusatzfeld lesen (unbekannte Hinweis-Codes entfallen); null = keine Telefon-Bestellung. */
export function readPhoneOrder(raw: unknown): StoredPhoneOrder | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const value = raw as Record<string, unknown>
  const hints = Array.isArray(value.hints)
    ? value.hints.flatMap((entry): StoredPhoneOrder['hints'] => {
        const hint = entry as { code?: unknown; message?: unknown }
        return typeof hint.code === 'string' && (PHONE_ORDER_HINTS as readonly string[]).includes(hint.code) && typeof hint.message === 'string'
          ? [{ code: hint.code as PhoneOrderHint, message: hint.message }]
          : []
      })
    : []
  return {
    accessId: text(value.accessId),
    callbackPhone: text(value.callbackPhone),
    phoneSource: value.phoneSource === 'caller_id' || value.phoneSource === 'other' ? value.phoneSource : null,
    phoneConfirmed: value.phoneConfirmed === true,
    ageConfirmed: value.ageConfirmed === true,
    callRef: text(value.callRef),
    placedAt: text(value.placedAt),
    late: value.late === true,
    contactName: text(value.contactName),
    hints,
  }
}

/** Zahlarten am Telefon: privat bar oder Karte bei der Übergabe; Geschäftskunden die vereinbarte Bedingung (TB-4, TB-6) */
export const PHONE_PAYMENT_METHODS = ['cash', 'ec'] as const
export type PhonePaymentMethod = (typeof PHONE_PAYMENT_METHODS)[number]

/** Kundennummer wie am Telefon genannt: groß, ohne Leerzeichen, `-`, `/`, `.`, `_` – „k 10 417“ → „K10417“ */
export const normalizeCustomerNumber = (value: string): string => value.toUpperCase().replace(/[\s\-/._]/g, '')

/** Stufe des Bestands einer Position: reicht doppelt, reicht gerade, reicht nicht (TB-12) */
export function stockLevel(availableBase: number | null, neededBase: number): 'ok' | 'low' | 'short' {
  if (availableBase === null) return 'ok'
  if (availableBase < neededBase) return 'short'
  return availableBase < 2 * neededBase ? 'low' : 'ok'
}

/** Liegt das Ja im laufenden Gespräch (≤ 2 Minuten)? Sonst ist es eine späte Übertragung aus der Warteschlange (TB-8). */
export const isLivePlacement = (placedAt: Date, now: Date): boolean => now.getTime() - placedAt.getTime() <= PHONE_ORDER_LIVE_MS
